import { apiGet, apiPost } from '../utils/api';

/** A structured advisory recommendation, as returned by the backend. */
export interface AdvisoryRecommendation {
    id: number;
    tenant_id: number;
    farmer_id: number | null;
    farm_id: number | null;
    field_id: number | null;
    crop_id: number | null;
    risk_type: string | null;
    risk_level: 'low' | 'medium' | 'high' | 'critical';
    answer: string;
    recommendation: string;
    why: string[];
    data_used: string[];
    uncertainty: string | null;
    confidence: number | null;
    requires_human_review: boolean;
    review_reason: string | null;
    status: string;
    model_version: string | null;
    safety_version: string | null;
    image_url: string | null;
    created_at: string;
}

export interface MorningRoundsField {
    fieldId: number;
    fieldName: string | null;
    cropType: string | null;
    district: string | null;
    farmId: number | null;
    farmerName: string | null;
    openIssues: number;
    overdueAssignments: number;
    pendingRecommendations: number;
    daysSinceLastObservation: number | null;
    highestRiskLevel: string | null;
    score: number;
    level: 'high' | 'review' | 'normal';
    reasons: string[];
}

export interface MorningRoundsResponse {
    buckets: { assessed: number; high: number; needReview: number; normal: number };
    fields: MorningRoundsField[];
    unavailable: boolean;
}

export interface IntelligenceMetrics {
    farmsMonitored: number | null;
    fieldsMonitored: number | null;
    highRiskFields: number | null;
    fieldsNeedingReview: number | null;
    openRecommendations: number | null;
    recommendationsWaitingForAgronomist: number | null;
    overdueFieldTasks: number | null;
    openAgronomicIssues: number | null;
    completedRecommendationActions: number | null;
    weatherSensitiveOperations: number | null;
    harvestReadyFields: number | null;
}

export interface IntelligenceResponse {
    metrics: IntelligenceMetrics;
    filters: Record<string, unknown>;
}

export interface FieldDrilldown {
    field: Record<string, any>;
    observations: Record<string, any>[];
    recommendations: AdvisoryRecommendation[];
    assignments: Record<string, any>[];
    reports: Record<string, any>[];
}

export interface MorningRoundsFilters {
    farm_id?: string;
    crop?: string;
    region?: string;
}

export const fetchMorningRounds = async (
    filters: MorningRoundsFilters = {},
): Promise<MorningRoundsResponse> => {
    const params: Record<string, string> = {};
    if (filters.farm_id) params.farm_id = filters.farm_id;
    if (filters.crop) params.crop = filters.crop;
    if (filters.region) params.region = filters.region;
    return apiGet<MorningRoundsResponse>('/api/advisory/morning-rounds', params);
};

export const fetchFieldIntelligence = async (
    filters: Record<string, string> = {},
): Promise<IntelligenceResponse> =>
    apiGet<IntelligenceResponse>('/api/advisory/intelligence', filters);

export const fetchRecommendations = async (
    params: { status?: string; field_id?: string } = {},
): Promise<AdvisoryRecommendation[]> => {
    const res = await apiGet<{ items: AdvisoryRecommendation[] }>('/api/advisory/recommendations', params);
    return res.items || [];
};

export const fetchRecommendation = async (id: number): Promise<Record<string, any>> =>
    apiGet<Record<string, any>>(`/api/advisory/recommendations/${id}`);

export const fetchFieldDrilldown = async (fieldId: number): Promise<FieldDrilldown> =>
    apiGet<FieldDrilldown>(`/api/advisory/fields/${fieldId}/drilldown`);

export type ReviewDecision = 'approve' | 'modify' | 'dismiss' | 'escalate' | 'create_task';

export const reviewRecommendation = async (
    id: number,
    decision: ReviewDecision,
    payload: {
        notes?: string;
        final_recommendation?: string;
        assignment?: Record<string, unknown>;
    } = {},
): Promise<Record<string, any>> =>
    apiPost<Record<string, any>>(`/api/advisory/recommendations/${id}/review`, {
        decision,
        ...payload,
    });
